import { deserialize, DocumentObject } from '@jsonapiworld/jsonapi-serializer';
import axios from 'axios';
import { backendApiGateway } from '~/client/backend';
import { Company } from '~/types/company';
import { OcrPageResponse, OcrPageResult, OcrSubmitResponse, OcrUploadUrlResponse } from '~/types/f24Ocr';
import { F24OcrUpload, F24UploadStatus } from '~/types/taxes';
import { buildJsonAPIQueryParams, jsonAPIFilterBuilder, jsonAPISortBuilder } from '~/utils/jsonapi-query';

// pages_count: how many pages there in the pdf
// pages: list of integer telling which pages are extracted
// pages can be not sequential (e.g. [1, 3, 5]) due to duplicated pages
export interface F24OcrInfo {
  ocr_job_id: string;
  file_name: string;
  name: string;
  status: F24UploadStatus;
  pdf_url: string;
  total_amount: string | null;
  payment_date: string | null;
  model_type: string | null;
  pages_count: number | null;
  pages: number[];
}

// Statuses we surface in the UI: in-progress (waiting OCR) and ready-for-review.
// Anything else (REVIEWED / CANCELED) is filtered out at the API level.
const F24_OCR_LIST_STATUSES: F24UploadStatus[] = ['WAITING_OCR', 'WAITING_REVIEW'];

export const fetchF24OcrList = async (companyId: Company['id']): Promise<F24OcrUpload[]> => {
  const filterBuilder = jsonAPIFilterBuilder();
  filterBuilder.in('status', F24_OCR_LIST_STATUSES);

  const sortingBuilder = jsonAPISortBuilder([{ sort: 'createdAt', direction: 'desc' }]);

  const params = buildJsonAPIQueryParams(filterBuilder, sortingBuilder);

  const { data } = await backendApiGateway.get<DocumentObject>(`/api/v1/f24/${companyId}/ocr`, {
    params,
    headers: { Accept: 'application/vnd.api+json' },
  });

  return deserialize<F24OcrUpload>(data) as F24OcrUpload[];
};

/**
 * Step 1 of the presigned upload flow: ask the BE for a presigned S3 URL.
 * The returned `ocr_job_id` is used for every later step (submit, show,
 * polling, download); `upload_url` is where the PDF bytes go (step 2).
 */
export const requestF24OcrUploadUrl = async (
  companyId: Company['id'],
  { name, fileName }: { name: string; fileName: string },
): Promise<OcrUploadUrlResponse> => {
  const { data } = await backendApiGateway.post<OcrUploadUrlResponse>(`/api/v1/f24/${companyId}/ocr/upload-url`, {
    name,
    file_name: fileName,
  });

  return data;
};

/**
 * Step 2: upload the PDF bytes straight to S3 via the presigned URL.
 * `Content-Type` MUST be exactly `application/pdf`: it is part of the presigned
 * signature, so any other value (or letting the browser choose) makes S3 reject
 * the PUT with 403 SignatureDoesNotMatch. Bare axios on purpose — no Sibill auth
 * headers here, the URL signature is the authorization.
 */
export const uploadF24OcrPdfToS3 = async (uploadUrl: string, file: File): Promise<void> => {
  await axios.put(uploadUrl, file, { headers: { 'Content-Type': 'application/pdf' } });
};

/**
 * Step 3: notify the BE that the file is on S3. The BE verifies it, publishes
 * the OCR request and moves the job to WAITING_OCR.
 */
export const submitF24OcrUpload = async (companyId: Company['id'], ocrJobId: string): Promise<OcrSubmitResponse> => {
  const { data } = await backendApiGateway.post<OcrSubmitResponse>(`/api/v1/f24/${companyId}/ocr/submit`, {
    ocr_job_id: ocrJobId,
  });

  return data;
};

export const getF24OcrDownloadUrl = async (companyId: Company['id'], ocrJobId: string): Promise<string> => {
  const {
    data: { url },
  } = await backendApiGateway.get<{ url: string }>(`/api/v1/f24/${companyId}/ocr/${ocrJobId}/download`);

  return url;
};

export const getF24OcrPage = async (
  companyId: Company['id'],
  ocrJobId: string,
  page: number,
): Promise<OcrPageResponse> => {
  const { data } = await backendApiGateway.get<OcrPageResponse>(
    `/api/v1/f24/${companyId}/ocr/${ocrJobId}/pages/${page}`,
  );

  return data;
};

export const getF24OcrInfo = async (companyId: Company['id'], ocrJobId: string): Promise<F24OcrInfo> => {
  const { data } = await backendApiGateway.get<F24OcrInfo>(`/api/v1/f24/${companyId}/ocr/${ocrJobId}`);

  return data;
};

export const deleteF24OcrUpload = async (companyId: Company['id'], ocrJobId: string): Promise<void> => {
  await backendApiGateway.delete(`/api/v1/f24/${companyId}/ocr/${ocrJobId}`);
};

export const saveF24OcrPage = async (
  companyId: Company['id'],
  ocrJobId: string,
  page: number,
  result: OcrPageResult,
): Promise<{ status: 'saved' }> => {
  const { data } = await backendApiGateway.put<{ status: 'saved' }>(
    `/api/v1/f24/${companyId}/ocr/${ocrJobId}/pages/${page}`,
    result,
  );

  return data;
};
